import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest, filter, takeUntil, withLatestFrom } from 'rxjs';
import { CodeAndDescription } from '../../../create-request/models/shared/CodeAndDescription';
import { selectCheckCountries, selectCheckReasonMap, selectCheckRecipientSelection, selectDefaultPayee, selectPayeeType, selectStateList, selectCheckDefaultRecipient, selectIsFromEditInProgress, selectOpenTwePopupConditions, selectUseConstructedPayee, selectStandingAuthCheckInfo, selectCheckPayee, selectSaModifiedPayeeCheckbox, selectIsFromTableOrView, selectShouldDisableFieldForEditQcd, selectIsFromEditStarted } from '../../reducers';
import { StandingAuthFormActions, StandingAuthReviewPageActions } from '../../actions';
import { selectIsIra, selectRegistrationType } from '../../../account-info-header/reducers';
import { CheckStandingAuthDetails } from '../../models/standingAuthorizationForm';
import { buildFromPrefilledForm } from '../../../create-request/utilities/formUtilities';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { FirstPartiesOutput } from '../../../modified-first-parties/models/FirstParties';
import { selectModifiedFirstParties } from '../../../modified-first-parties/reducers';
import { Constants } from '../../../shared/utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-details-check',
  templateUrl: './sa-details-check.component.html',
  styleUrls: ['./sa-details-check.component.scss']
})
export class StandingAuthorizationDetailsCheckComponent implements OnInit {

  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPromptsObs: Observable<CheckStandingAuthDetails>;
  standingAuthorizationDetailsGroup: UntypedFormGroup;
  payeeType$: Observable<string>;
  defaultPayee$: Observable<string[]>;
  validPayeeType: boolean;
  destroy$: Subject<boolean> = new Subject();
  recipientSelection$: Observable<string>;
  validRecipient: boolean;
  stateList$: Observable<CodeAndDescription[]>;
  countryList$: Observable<CodeAndDescription[]>;
  checkReasonMap$: Observable<CodeAndDescription[]>;
  defaultRecipient$: Observable<string[]>;
  isIra$: Observable<boolean>;
  isFromEdit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  useModifiedPayee: boolean;
  useConstructedPayee: boolean;
  firstPartyPayees$: Observable<FirstPartiesOutput[]>;
  isFirstPartyModified: boolean;
  showModifiedPayeeBanner: boolean = false;
  jointAcctModifiedPayeeBannerText: string = Constants.jointPayeeBannerText;
  disableFieldsForEditQcd$: Observable<boolean>;

  constructor(private fb: UntypedFormBuilder, private store: Store, private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    this.standingAuthorizationDetailsGroup = this.fb.group({});
    this.parentForm.setControl('standingAuthorizationDetails', this.standingAuthorizationDetailsGroup, { emitEvent: false });

    this.payeeType$ = this.store.select(selectPayeeType);
    this.recipientSelection$ = this.store.select(selectCheckRecipientSelection);
    this.stateList$ = this.store.select(selectStateList);
    this.countryList$ = this.store.select(selectCheckCountries);
    this.checkReasonMap$ = this.store.select(selectCheckReasonMap);
    this.defaultRecipient$ = this.store.select(selectCheckDefaultRecipient);
    this.isIra$ = this.store.select(selectIsIra);
    this.defaultPayee$ =
      this.isFirstPartyModified &&
        !this.useConstructedPayee
        ? this.store.select(selectCheckPayee)
        : this.store.select(selectDefaultPayee);

    this.disableFieldsForEditQcd$ = this.store.select(selectShouldDisableFieldForEditQcd);

    // If payee type is valid, show the payee name
    this.payeeType$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(payeeType => {
      if (payeeType) {
        this.validPayeeType = true;
      } else {
        this.validPayeeType = false;
      }
    })

    // If recipient is valid, show the rest of the transaction details
    this.recipientSelection$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(recipient => {
      if (recipient) {
        this.validRecipient = true;
      } else {
        this.validRecipient = false;
      }
    });

    combineLatest([
      this.store.select(selectRegistrationType),
      this.store.select(selectSaModifiedPayeeCheckbox)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([type, checked]) => {
        this.useModifiedPayee = checked ?? this.useModifiedPayee;
        this.showModifiedPayeeBanner = type === 'Jt Ten' && checked ? true : false;
      });

    this.store.select(selectPayeeType).pipe(takeUntil(this.destroy$)).subscribe(payeeType => {
      if (payeeType === 'thirdParty') {
        this.useModifiedPayee = false;
        let modifiedCheckbox = this.parentForm.controls.standingAuthorizationDetails.get('modifiedPayeeCheckbox') as UntypedFormControl;
        modifiedCheckbox?.reset();
      }
    });

    this.store.select(selectStandingAuthCheckInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.useConstructedPayee = info.useConstructedPayee;
        this.isFirstPartyModified = info.isFirstPartyModified;

      });

    this.firstPartyPayees$ = this.store.select(selectModifiedFirstParties);

    this.store.select(selectOpenTwePopupConditions).pipe(
      takeUntil(this.destroy$)
    ).subscribe((tweConditions) => {
      const { verifyCallFinished, openTwePopup } = tweConditions;
      if (verifyCallFinished && !openTwePopup)
        this.store.dispatch(StandingAuthFormActions.loadReviewPage());
    });

    this.prefilledPromptsObs
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEditStarted),
          this.store.select(selectIsFromEditInProgress),
        ),
        filter(([prefilledPrompts, isStarted, inProgress]) => !!prefilledPrompts && (isStarted || inProgress))
      )
      .subscribe(([prefilledPrompts, _isFromEdit]) => {
        buildFromPrefilledForm(
          prefilledPrompts,
          this.standingAuthorizationDetailsGroup
        );
        this.isFromEdit$.next(true);
        this.store.dispatch(StandingAuthReviewPageActions.editCompleted());
      });
  }

  get selectedDistributionReason() {
    return this.parentForm?.controls.distributionReason;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
